import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  Clock,
  FolderOpen,
  CheckSquare,
  BarChart3,
  Users,
  Activity,
  CalendarDays,
  Link2,
  Umbrella,
  Mail,
  MessageCircle,
  Inbox,
  Megaphone,
  ScrollText,
  ChevronRight,
  Briefcase,
  CalendarRange,
  PieChart,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUnreadCount, useIncomingChatRequests, useInboxUnreadCount } from '../hooks/api';
import { ROUTES } from '../constants/routes';
import Avatar from './Avatar';
import SpotifyIcon from './spotify/SpotifyIcon';
import { GitHubIcon, OutlookIcon } from './icons/BrandIcons';

const GREETING_LINES = [
  "Make it count",
  "Time to shine",
  "Ready to crush it?",
  "Start strong",
  "Every second counts",
  "Stay focused",
  "Let's get it done",
  "Make today count",
  "Track smart",
  "Productive day",
  "Ready?",
  "Let's do this",
  "Your time, your way",
  "Focus mode: on",
  "Win today",
];

const Sidebar = ({ onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { data: chatUnread } = useUnreadCount();
  const { data: inboxUnread } = useInboxUnreadCount();
  const { data: incomingChatRequests = [] } = useIncomingChatRequests();
  const pendingRequestCount = (incomingChatRequests || []).filter(
    (r) => r.status === 'pending' || !r.status
  ).length;
  const chatBadge = (chatUnread?.unread_count || 0) + pendingRequestCount;
  const inboxBadge = inboxUnread?.unread_count || 0;
  const [expandedSections, setExpandedSections] = useState({
    work: true,
    projects: true,
    management: true,
    tools: true,
    support: true
  });
  // Collapsible per-item (parent -> children) state. Keyed by `basePath` so
  // navigating directly to a sub-route keeps the parent open on first paint.
  const [expandedItems, setExpandedItems] = useState({});

  // Auto-expand any parent whose basePath matches the current location so a
  // deep-link like /resource-utilization/insights opens its sub-nav on load.
  React.useEffect(() => {
    setExpandedItems((prev) => {
      const next = { ...prev };
      if (location.pathname.startsWith(ROUTES.RESOURCE_UTILIZATION)) {
        next[ROUTES.RESOURCE_UTILIZATION] = true;
      }
      return next;
    });
  }, [location.pathname]);

  const toggleItem = (basePath) => {
    setExpandedItems((prev) => ({ ...prev, [basePath]: !prev[basePath] }));
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [greetingLine] = useState(() => GREETING_LINES[Math.floor(Math.random() * GREETING_LINES.length)]);

  const getMenuSections = () => {
    const sections = {
      work: {
        title: 'Work',
        icon: CheckSquare,
        items: [
          { path: '/dashboard', icon: CheckSquare, label: user?.role === 'member' ? 'Dashboard' : 'Analytics' },
          { path: '/time-entries', icon: Clock, label: 'Time Entries' },
          { path: '/timesheet', icon: CalendarDays, label: 'Timesheet' },
          { path: ROUTES.INBOX, icon: Inbox, label: 'Inbox', badge: inboxBadge },
          { path: ROUTES.MESSAGES, icon: MessageCircle, label: 'Messages', badge: chatBadge },
        ]
      },
      projects: {
        title: 'Workspace',
        icon: FolderOpen,
        items: [
          { path: '/projects', icon: FolderOpen, label: 'Projects' },
          { path: '/tasks', icon: CheckSquare, label: 'Tasks' },
        ]
      },
      management: {
        title: 'Management',
        icon: Users,
        items: []
      },
      tools: {
        title: 'Connected Apps',
        icon: Link2,
        items: [
          { path: '/calendar', icon: OutlookIcon, label: 'Outlook Calendar' },
          { path: '/github-integration', icon: GitHubIcon, label: 'GitHub', iconClassName: 'text-secondary-900 dark:text-white' },
          { path: ROUTES.SPOTIFY, icon: SpotifyIcon, label: 'Spotify', iconClassName: 'text-[#1DB954]' },
        ]
      },
      support: {
        title: 'Support',
        icon: Activity,
        items: [
          { path: '/help', icon: Activity, label: 'Pulse AI' },
        ]
      }
    };

    // Add management items based on role
    if (user?.role === 'admin' || user?.role === 'project_lead') {
      sections.management.items.push({ path: '/team', icon: Users, label: 'Team' });
    }

    // Add leave management for all users
    sections.management.items.push({ path: '/leave-management', icon: Umbrella, label: 'Leave' });

    // Add admin-only items
    if (user?.role === 'admin') {
      sections.management.items.push(
        { path: ROUTES.ANNOUNCEMENTS_ADMIN, icon: Megaphone, label: 'Announcements' },
        { path: '/attendance-management', icon: Clock, label: 'Attendance' },
        {
          // Clicking the parent takes the admin to the People sub-view so
          // they don't land on an empty router outlet. The sidebar expands
          // automatically whenever any `/resource-utilization/*` route is
          // active (see collapsible rendering below).
          path: ROUTES.RESOURCE_UTIL_PEOPLE,
          icon: BarChart3,
          label: 'Resource Utilization',
          // Any pathname starting with `basePath` highlights the parent and
          // keeps the sub-nav expanded.
          basePath: ROUTES.RESOURCE_UTILIZATION,
          children: [
            { path: ROUTES.RESOURCE_UTIL_PEOPLE, icon: Users, label: 'People' },
            { path: ROUTES.RESOURCE_UTIL_PROJECTS, icon: Briefcase, label: 'Projects' },
            { path: ROUTES.RESOURCE_UTIL_TIMELINE, icon: CalendarRange, label: 'Timeline' },
            { path: ROUTES.RESOURCE_UTIL_INSIGHTS, icon: PieChart, label: 'Insights' },
          ],
        },
        { path: '/admin-dashboard', icon: Users, label: 'Monitoring & Analysis' },
        { path: ROUTES.ADMIN_MESSAGE_LOGS, icon: ScrollText, label: 'Message Logs' },
        { path: '/promotional-emails', icon: Mail, label: 'Promotional Emails' }
      );
    }

    return sections;
  };

  const menuSections = getMenuSections();

  // Filter sections based on search query. Children's labels count too so
  // searching "insights" still surfaces its Resource Utilization parent.
  const filteredSections = React.useMemo(() => {
    if (!searchQuery.trim()) return menuSections;

    const q = searchQuery.toLowerCase();
    const filtered = {};
    Object.entries(menuSections).forEach(([key, section]) => {
      const filteredItems = section.items.filter((item) => {
        const labelMatch = item.label.toLowerCase().includes(q);
        const childMatch = (item.children || []).some((c) =>
          c.label.toLowerCase().includes(q),
        );
        return labelMatch || childMatch;
      });
      if (filteredItems.length > 0) {
        filtered[key] = { ...section, items: filteredItems };
      }
    });
    return filtered;
  }, [menuSections, searchQuery]);

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleMenuClick = (path) => {
    // Close mobile menu when clicking on a menu item
    if (onClose) {
      onClose();
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && searchQuery) {
      setSearchQuery('');
    }
  };

  // Auto-expand sections when searching
  React.useEffect(() => {
    if (searchQuery) {
      setExpandedSections(prev => ({
        ...prev,
        work: true,
        projects: true,
        management: true,
        tools: true,
        support: true
      }));
    }
  }, [searchQuery]);

  const displayName = user?.full_name || user?.username || 'there';
  const firstName = displayName.split(' ')[0] || displayName;

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-secondary-800 border-r border-secondary-200 dark:border-secondary-700 w-56 overflow-hidden">
      {/* User greeting - Mobile (with close button) */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
        <div 
          className="flex items-center gap-3 animate-fade-in-up cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => navigate('/profile')}
        >
          <div className="flex-shrink-0 rounded-full animate-scale-in">
            <Avatar 
              blobName={user?.avatar}
              previewable
              alt={displayName}
              size="sm"
              className="w-10 h-10"
              fallbackText={user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
            />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">Hey, {firstName}</p>
            <p className="text-xs text-secondary-600 dark:text-secondary-400 truncate">{greetingLine}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center text-secondary-600 hover:text-primary-600 transition-colors rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700"
          aria-label="Close menu"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* User greeting - Desktop (height matches Layout header h-16 lg:h-20 for border alignment) */}
      <div 
        className="hidden lg:flex items-center gap-3 px-4 h-16 lg:h-20 border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50/50 dark:bg-secondary-800 cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-all duration-300 group animate-fade-in-up"
        onClick={() => navigate('/profile')}
      >
        <div className="flex-shrink-0 rounded-full transition-all duration-300 animate-scale-in">
          <Avatar 
            blobName={user?.avatar}
            previewable
            alt={displayName}
            size="sm"
            className="w-10 h-10"
            fallbackText={user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-secondary-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">Hey, {firstName}</p>
          <p className="text-xs text-secondary-600 dark:text-secondary-400 truncate">{greetingLine}</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="p-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="sidebar-search"
          />
          <svg
            className="absolute left-2.5 top-2.5 h-4 w-4 text-secondary-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-2.5 h-4 w-4 text-secondary-400 hover:text-secondary-600"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <div className="space-y-1">
          {Object.keys(filteredSections).length === 0 && searchQuery ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-secondary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="mt-2 text-sm text-secondary-500">No results found</p>
              <p className="text-xs text-secondary-400">Try a different search term</p>
            </div>
          ) : (
            Object.entries(filteredSections).map(([sectionKey, section]) => {
              // Skip empty sections
              if (section.items.length === 0) return null;
              
              const SectionIcon = section.icon;
              const isExpanded = expandedSections[sectionKey] || searchQuery; // Auto-expand when searching
              
              return (
                <div key={sectionKey} className={`sidebar-section ${sectionKey === 'ai' ? 'ai-section' : ''}`}>
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(sectionKey)}
                    className={`sidebar-section-header ${isExpanded ? 'expanded' : ''}`}
                  >
                    <div className="section-title">
                      <SectionIcon className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{section.title}</span>
                    </div>
                    <svg
                      className="section-chevron h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Section Items */}
                  {isExpanded && (
                    <div className="sidebar-section-items">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                        const base = item.basePath || item.path;
                        const isActive = hasChildren
                          ? location.pathname.startsWith(base)
                          : location.pathname === item.path;
                        const isDisabled = item.disabled;

                        if (isDisabled) {
                          return (
                            <div
                              key={item.path}
                              className={`sidebar-item disabled ${item.className || ''}`}
                            >
                              <div className="item-content">
                                <Icon className="item-icon" />
                                <span className="item-label">{item.label}</span>
                                {item.badge && (
                                  <span className="text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-1 rounded-full animate-pulse flex-shrink-0">
                                    {item.badge}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        }

                        if (hasChildren) {
                          const itemExpanded =
                            expandedItems[base] ?? location.pathname.startsWith(base);
                          return (
                            <div key={item.path} className="sidebar-item-with-children">
                              <div
                                className={`sidebar-item ${isActive ? 'active' : ''} ${item.className || ''}`}
                                data-testid={`sidebar-parent-${base}`}
                              >
                                <Link
                                  to={item.path}
                                  onClick={() => {
                                    setExpandedItems((prev) => ({ ...prev, [base]: true }));
                                    handleMenuClick(item.path);
                                  }}
                                  className="item-content flex-1 min-w-0"
                                >
                                  <Icon className={`item-icon ${item.iconClassName || ''}`} />
                                  <span className="item-label">{item.label}</span>
                                </Link>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleItem(base);
                                  }}
                                  className="p-1 ml-1 rounded hover:bg-secondary-100 dark:hover:bg-secondary-700 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 flex-shrink-0"
                                  aria-expanded={itemExpanded}
                                  aria-label={itemExpanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
                                  data-testid={`sidebar-parent-toggle-${base}`}
                                >
                                  <ChevronRight
                                    className={`h-3.5 w-3.5 transition-transform duration-150 ${itemExpanded ? 'rotate-90' : ''}`}
                                  />
                                </button>
                              </div>
                              {itemExpanded && (
                                <div
                                  className="ml-5 pl-2 mt-0.5 border-l border-secondary-200 dark:border-secondary-700 space-y-0.5"
                                  data-testid={`sidebar-children-${base}`}
                                >
                                  {item.children.map((child) => {
                                    const ChildIcon = child.icon;
                                    const childActive = location.pathname === child.path;
                                    return (
                                      <Link
                                        key={child.path}
                                        to={child.path}
                                        onClick={() => handleMenuClick(child.path)}
                                        className={`sidebar-item sidebar-child-item ${childActive ? 'active' : ''}`}
                                        data-testid={`sidebar-child-${child.path}`}
                                      >
                                        <div className="item-content">
                                          <ChildIcon className="item-icon h-3.5 w-3.5" />
                                          <span className="item-label">{child.label}</span>
                                        </div>
                                      </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        }

                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => handleMenuClick(item.path)}
                            className={`sidebar-item ${isActive ? 'active' : ''} ${item.className || ''}`}
                          >
                            <div className="item-content">
                              <Icon className={`item-icon ${item.iconClassName || ''}`} />
                              <span className="item-label">{item.label}</span>
                              {Number(item.badge) > 0 && (
                                <span
                                  className={`text-xs text-white px-1.5 py-0.5 rounded-full flex-shrink-0 min-w-[18px] text-center ${
                                    item.path === ROUTES.INBOX
                                      ? 'bg-primary-600'
                                      : item.path === ROUTES.MESSAGES
                                        ? 'bg-red-500'
                                        : 'bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse'
                                  }`}
                                >
                                  {Number(item.badge) > 99 ? '99+' : item.badge}
                                </span>
                              )}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
